/**
 * What a place on the Stages and queues picture is doing, from the facts the API sends. The rule the screen
 * exists for: red means staff must act and nothing else. Work held by design (no credits, a job not set up or
 * switched off, a person's turn) and work parked for its customer to clear are shown as holds, never as a fault;
 * amber is work that waited past its stage's time or is being tried again. Pure.
 */
import { isReasonCode, reasons } from '@paige/format';
import type { AdminFlow, FlowCause, FlowCell, FlowRun } from './flow-types';

/** From most to least urgent; a place shows the first that applies. */
export type FlowTone = 'stuck' | 'slow' | 'busy' | 'moving' | 'held' | 'idle';
export type FlowNoteKind = 'ours' | 'slow' | 'retry' | 'theirs' | 'hold';

/** One reason a place is not simply flowing, for a chip on it and a row in its detail. */
export interface FlowNote {
  kind: FlowNoteKind;
  tone: 'stuck' | 'slow' | 'held';
  count: number;
  /** A reason code, or null for a plain slow note. */
  reason: string | null;
  /** Age of the oldest item it speaks of. */
  seconds: number;
  cause: FlowCause | null;
}

export interface FlowStatus {
  tone: FlowTone;
  notes: FlowNote[];
  /** Everything at the place now: waiting, being worked, held, tried again and parked. */
  here: number;
  /** Minutes of work queued at the present rate; null when nothing is moving or nothing waits. */
  queuedMinutes: number | null;
}

const NOTE_TONE: Record<FlowNoteKind, FlowNote['tone']> = {
  ours: 'stuck',
  slow: 'slow',
  retry: 'slow',
  theirs: 'held',
  hold: 'held',
};
const ORDER: FlowNoteKind[] = ['ours', 'slow', 'retry', 'theirs', 'hold'];

/** A reading with no progress for this long is slow (paige-watch's own setup check waits as long). */
export const QUIET_RUN_SECONDS = 600;

/** Whether a parked cause is the customer's to clear (the reason catalogue's owner); anything unknown is ours. */
export function isTheirs(reason: string): boolean {
  return isReasonCode(reason) && reasons[reason].owner === 'customer';
}

const note = (kind: FlowNoteKind, c: FlowCause): FlowNote => ({
  kind,
  tone: NOTE_TONE[kind],
  count: c.count,
  reason: c.reason,
  seconds: c.oldest.seconds,
  cause: c,
});

function slowNote(cell: FlowCell): FlowNote[] {
  if (cell.slow <= 0) return [];
  const seconds = Math.max(
    cell.oldestWaiting?.seconds ?? 0,
    ...cell.retries.map((r) => r.oldest.seconds),
  );
  return [{ kind: 'slow', tone: 'slow', count: cell.slow, reason: null, seconds, cause: null }];
}

/** Every reason the place is not simply flowing, the most urgent first. */
export function notesOf(cell: FlowCell): FlowNote[] {
  const notes = [
    ...cell.parked.map((c) => note(isTheirs(c.reason) ? 'theirs' : 'ours', c)),
    ...slowNote(cell),
    ...cell.retries.map((c) => note('retry', c)),
    ...cell.holds.map((c) => note('hold', c)),
  ];
  return notes.sort((a, b) => ORDER.indexOf(a.kind) - ORDER.indexOf(b.kind) || b.count - a.count);
}

const sum = (causes: FlowCause[]) => causes.reduce((n, c) => n + c.count, 0);

/** Whether pages left the place in the last three steps: a stage goes quiet soon after its work has passed. */
export function movedJustNow(cell: FlowCell): boolean {
  return cell.ticks.slice(-3).some((pages) => pages > 0);
}

function toneOf(cell: FlowCell, notes: FlowNote[], queuedMinutes: number | null): FlowTone {
  if (notes.some((n) => n.tone === 'stuck')) return 'stuck';
  if (notes.some((n) => n.tone === 'slow')) return 'slow';
  if (queuedMinutes !== null && queuedMinutes > 1) return 'busy';
  if (cell.working > 0 || cell.waiting > 0 || movedJustNow(cell)) return 'moving';
  return notes.length > 0 ? 'held' : 'idle';
}

export function statusOf(cell: FlowCell): FlowStatus {
  const notes = notesOf(cell);
  const queuedMinutes =
    cell.waiting > 0 && cell.pagesPerMinute > 0 ? cell.waitingPages / cell.pagesPerMinute : null;
  return {
    tone: toneOf(cell, notes, queuedMinutes),
    notes,
    here: cell.waiting + cell.working + sum(cell.holds) + sum(cell.retries) + sum(cell.parked),
    queuedMinutes,
  };
}

/** A reading that has made no progress for ten minutes. */
export function runIsQuiet(run: FlowRun): boolean {
  return run.quietSeconds >= QUIET_RUN_SECONDS;
}

/** The engine lane's status: its hand-offs' own, and slow when a reading has gone quiet. */
export function engineStatusOf(cell: FlowCell, runs: FlowRun[]): FlowStatus {
  const status = statusOf(cell);
  if (status.tone === 'stuck' || status.tone === 'slow') return status;
  if (runs.some(runIsQuiet)) return { ...status, tone: 'slow' };
  return runs.length > 0 && (status.tone === 'idle' || status.tone === 'held')
    ? { ...status, tone: 'moving' }
    : status;
}

export interface BoardPulse {
  live: boolean;
  /** Pages a minute arriving on both roads, and leaving export. */
  arriving: number;
  leaving: number;
  /** Items being worked anywhere, the engine's readings among them. */
  working: number;
}

/** Whether anything is in flight, for the headline: "Nothing in flight" is said only when it is true. */
export function pulseOf(flow: AdminFlow): BoardPulse {
  const cell = (place: string) => flow.cells.find((c) => c.place === place);
  const arriving =
    (cell('arriving_api')?.pagesPerMinute ?? 0) + (cell('arriving_engine')?.pagesPerMinute ?? 0);
  const leaving = cell('export')?.pagesPerMinute ?? 0;
  const working =
    flow.cells.filter((c) => c.place !== 'engine').reduce((n, c) => n + c.working, 0) +
    Math.max(flow.runs.length, cell('engine')?.working ?? 0);
  const moving = flow.cells.some((c) => c.waiting > 0 || movedJustNow(c));
  return { live: working > 0 || moving, arriving, leaving, working };
}
